'use client'

import { createContext, useContext } from 'react'

import type { ColumnDef, SortingState, Table } from '@tanstack/react-table'

// ============================================================================
// Types
// ============================================================================

export type LoadStrategy = 'lazy' | 'eager'

export interface DataTableContextValue<TData> {
  /** TanStack Table instance */
  table: Table<TData>
  /** Column definitions */
  columns: ColumnDef<TData, unknown>[]
  /** All loaded data items */
  data: TData[]
  /** Total count from API (not loaded count) */
  totalCount: number
  /** Current loading state */
  isLoading: boolean
  /** Whether we're fetching the next page */
  isFetchingNextPage: boolean
  /** Whether more pages are available */
  hasNextPage: boolean
  /** Function to fetch next page */
  fetchNextPage: () => void
  /** Function to fetch ALL remaining pages (for sort/filter) */
  fetchAllPages: () => Promise<void>
  /** Whether all data has been loaded */
  isFullyLoaded: boolean
  /** Current load strategy */
  loadStrategy: LoadStrategy
  /** Current sorting state */
  sorting: SortingState
  /** Set sorting state */
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  /** Container ref for virtual scrolling */
  containerRef: React.RefObject<HTMLDivElement>
  /** Estimated row height for virtualization */
  estimatedRowHeight: number
}

// ============================================================================
// Context
// ============================================================================

export const DataTableContext =
  createContext<DataTableContextValue<unknown> | null>(null)

/**
 * Hook to access DataTable context
 * Throws if used outside of DataTableV2.Root
 */
export function useDataTableContext<TData>(): DataTableContextValue<TData> {
  const ctx = useContext(DataTableContext)
  if (!ctx) {
    throw new Error('useDataTableContext must be used within DataTableV2.Root')
  }
  return ctx as DataTableContextValue<TData>
}
